"use client";

import type { AudioSample } from "@mw/shared/payload-types";
import type { ContentSlice } from "@/app/_blocks/BlockAudioPlayerWithTranscript/types";
import type { ChatTranscriptPlaybackVariantInput } from "@/app/_ui/components/core/ChatTranscript";

import clsx from "clsx";
import { Howl } from "howler";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tabs, type Key } from "react-aria-components";

import { PlayPauseButton } from "@/app/_ui/components/core/PlayPauseButton";
import { TabList } from "@/app/_ui/components/core/TabList";
import { WordHighlighter } from "@/app/_ui/components/demos/TTS/WordHighlighter";
import { createContentSlices } from "@/app/_blocks/BlockAudioPlayerWithTranscript/utils";
import { getAudioUrl } from "@utilities/payload";

interface ExtendedMediaElement {
  addEventListener: (event: string, handler: () => void) => void;
  currentTime: number;
  duration: number;
  paused: boolean;
  removeEventListener: (event: string, handler: () => void) => void;
}

interface PreparedAudioItem {
  id: string;
  title: string;
  audioUrl: string;
  parsedContent: ContentSlice[];
}

export interface AudioTranscriptDemoProps {
  items: AudioSample[];
  playbackVariant?: ChatTranscriptPlaybackVariantInput;
  className?: string;
}

function prepareAudioItem(item: AudioSample, index: number): PreparedAudioItem {
  const parsedContent = createContentSlices(
    item.sttResponse ? JSON.stringify(item.sttResponse) : null,
    item.displayText || [],
  );

  return {
    id: item.id || `audio-item-${index}`,
    title: item.title || `Sample ${index + 1}`,
    audioUrl: getAudioUrl(item.audio) || "",
    parsedContent,
  };
}

// Simple word renderer for the transcript demo - highlights in dark text style
const TranscriptWordRenderer = ({
  word,
  highlighted,
  playbackVariant,
  hasBeenSpoken = true,
}: {
  word: string;
  highlighted: boolean;
  playbackVariant?: ChatTranscriptPlaybackVariantInput;
  hasBeenSpoken?: boolean;
}) => {
  const shouldFade = playbackVariant === "fadeUnspokenWords" && !hasBeenSpoken;

  return (
    <span
      className={clsx("tw-transition-colors tw-duration-100", {
        "tw-bg-black/10 tw-rounded-sm": highlighted,
        "tw-text-black/40": shouldFade,
      })}
    >
      {word}
    </span>
  );
};

export function AudioTranscriptDemo({ items, playbackVariant, className }: AudioTranscriptDemoProps) {
  const [selectedKey, setSelectedKey] = useState<Key | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const howlRef = useRef<Howl | null>(null);
  const mediaElementRef = useRef<ExtendedMediaElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Prepare items with parsed content
  const preparedItems = useMemo(() => items.map(prepareAudioItem), [items]);

  // Initialize selectedKey to first item if not set
  const effectiveSelectedKey = selectedKey ?? preparedItems[0]?.id ?? null;
  const currentItem = preparedItems.find(item => item.id === effectiveSelectedKey);

  // Get the first content slice that has a transcript
  const currentContentSlice = currentItem?.parsedContent?.find(slice => slice.transcript && slice.sttSlice);
  const displayText = currentContentSlice?.transcript || currentItem?.parsedContent?.[0]?.transcript || "";

  // Progress tracking with animation frame
  const startProgressTracking = useCallback(() => {
    const updateProgress = () => {
      if (howlRef.current && howlRef.current.playing()) {
        animationFrameRef.current = requestAnimationFrame(updateProgress);
      }
    };
    animationFrameRef.current = requestAnimationFrame(updateProgress);
  }, []);

  const stopProgressTracking = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  // Create Howl instance on demand
  const createHowlInstance = useCallback(
    (audioUrl: string) => {
      if (howlRef.current) {
        howlRef.current.unload();
      }

      setIsLoading(true);

      const howl = new Howl({
        src: [audioUrl],
        html5: true,
        preload: false,
        onload: () => {
          setIsLoading(false);
        },
        onplay: () => {
          setIsPlaying(true);
          startProgressTracking();
        },
        onpause: () => {
          setIsPlaying(false);
          stopProgressTracking();
        },
        onstop: () => {
          setIsPlaying(false);
          stopProgressTracking();
        },
        onend: () => {
          setIsPlaying(false);
          stopProgressTracking();
        },
        onloaderror: () => {
          setIsLoading(false);
        },
        onplayerror: () => {
          setIsPlaying(false);
          setIsLoading(false);
          stopProgressTracking();
        },
      });

      howlRef.current = howl;

      // Create a simple media element wrapper that WordHighlighter can use
      mediaElementRef.current = {
        get currentTime() {
          return howl.seek() || 0;
        },
        get duration() {
          return howl.duration() || 0;
        },
        get paused() {
          return !howl.playing();
        },
        addEventListener: () => {},
        removeEventListener: () => {},
      };

      return howl;
    },
    [startProgressTracking, stopProgressTracking],
  );

  // Handle selection change
  const handleSelectionChange = useCallback((key: Key) => {
    // Stop current playback
    if (howlRef.current) {
      howlRef.current.stop();
      howlRef.current.unload();
      howlRef.current = null;
    }
    setIsPlaying(false);
    setSelectedKey(key);
  }, []);

  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    if (!currentItem?.audioUrl) return;

    if (!howlRef.current) {
      const howl = createHowlInstance(currentItem.audioUrl);
      howl.load();
      howl.play();
      return;
    }

    if (isPlaying) {
      howlRef.current.pause();
    } else {
      howlRef.current.play();
    }
  }, [currentItem, isPlaying, createHowlInstance]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (howlRef.current) {
        howlRef.current.unload();
        howlRef.current = null;
      }
      stopProgressTracking();
    };
  }, [stopProgressTracking]);

  // Clean up when audio URL changes
  useEffect(() => {
    return () => {
      if (howlRef.current) {
        howlRef.current.unload();
        howlRef.current = null;
      }
    };
  }, [currentItem?.audioUrl]);

  if (preparedItems.length === 0) {
    return null;
  }

  return (
    <div className={clsx("tw-w-full tw-min-w-0 tw-flex tw-items-center tw-justify-center", className)}>
      <div className='tw-w-full tw-h-full tw-min-w-0 tw-flex tw-flex-col tw-gap-3'>
        {/* Transcript card */}
        <div
          className='tw-bg-white tw-overflow-hidden tw-rounded-[1.25rem] tw-relative tw-flex tw-flex-col'
          style={{ boxShadow: "0 0 1px rgb(0 0 0 / 0.4), 0 2px 2px rgb(0 0 0 / 0.04)" }}
        >
          {/* Transcript content - fixed height with scroll */}
          <div ref={scrollContainerRef} className='tw-h-32 sm:tw-h-40 tw-relative tw-overflow-y-auto tw-scrollbar-none'>
            <div className='tw-text-sm/6 tw-px-5 tw-py-4 tw-text-black'>
              {currentContentSlice?.sttSlice && isPlaying ? (
                <WordHighlighter
                  className='tw-whitespace-pre-wrap'
                  displayWords={currentContentSlice.sttSlice.displayWords}
                  endTimes={currentContentSlice.sttSlice.endTimes}
                  isPlaying={isPlaying}
                  mediaElement={mediaElementRef.current as any}
                  renderWord={(word, highlighted, wordIndex, isPast) => (
                    <TranscriptWordRenderer
                      key={wordIndex}
                      word={word}
                      highlighted={highlighted}
                      playbackVariant={playbackVariant}
                      hasBeenSpoken={isPast}
                    />
                  )}
                  startTimes={currentContentSlice.sttSlice.startTimes}
                  words={currentContentSlice.sttSlice.words}
                />
              ) : (
                <p className='tw-whitespace-pre-wrap'>{displayText}</p>
              )}
            </div>
          </div>

          {/* Play button */}
          <div className='tw-relative tw-flex tw-justify-end tw-bg-white tw-px-4 tw-pb-4'>
            <div className='tw-absolute tw-inset-x-0 tw-bottom-full tw-h-4 tw-pointer-events-none tw-bg-gradient-to-t tw-from-white tw-to-transparent' />
            <PlayPauseButton
              isPlaying={isPlaying}
              onClick={handlePlayPause}
              disabled={!currentItem?.audioUrl || isLoading}
              iconSize='sm'
              className='tw-size-10 tw-rounded-full tw-bg-cream tw-text-black'
            />
          </div>
        </div>

        {/* Tab selector */}
        {preparedItems.length > 1 && (
          <div className='tw-w-full tw-shrink-0'>
            <Tabs selectedKey={effectiveSelectedKey} onSelectionChange={handleSelectionChange}>
              <TabList items={preparedItems} surface='white' size='sm' centered />
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
}

AudioTranscriptDemo.displayName = "AudioTranscriptDemo";
